/**
 * om-card.js
 * Behaviour for the Product Grid card - variant pills, wishlist, add to cart.
 *
 * Listeners are delegated from `document`, not from the section wrapper, on
 * purpose: on a collection page Shopify's facets.js replaces the whole of
 * #ProductGridContainer every time a filter or sort changes. Anything bound to
 * elements inside it would be destroyed on the first filter click. Delegating
 * from document means filtering, sorting and pagination all keep working with
 * no re-binding.
 *
 * Per-card config is read from the nearest [data-om-grid] wrapper, so the
 * featured section and the collection page can be configured differently on
 * the same page without interfering.
 */
(function () {
  'use strict';

  if (window.__omCardBound) return;
  window.__omCardBound = true;

  var WISHLIST_KEY = 'om-wishlist';

  function cfg(el, key, fallback) {
    var root = el.closest('[data-om-grid]');
    if (!root) return fallback;
    var v = root.getAttribute('data-om-' + key);
    return v === null ? fallback : v;
  }

  function getWishlist() {
    try { return JSON.parse(window.localStorage.getItem(WISHLIST_KEY)) || []; }
    catch (e) { return []; }
  }

  function setWishlist(list) {
    try { window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); }
    catch (e) {}
  }

  /** Re-apply saved wishlist hearts. Runs again after every filter render. */
  function paintWishlist() {
    var saved = getWishlist();
    document.querySelectorAll('[data-om-wishlist]').forEach(function (btn) {
      var on = saved.indexOf(btn.getAttribute('data-product-id')) !== -1;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', String(on));
    });
  }

  // ------------------------------------------------------ variant pills ---

  document.addEventListener('click', function (e) {
    var pill = e.target.closest('[data-om-variant-pill]');
    if (!pill) return;

    var card = pill.closest('.om-card');
    var group = pill.closest('[data-om-variants]');
    if (!card || !group) return;

    group.querySelectorAll('[data-om-variant-pill]').forEach(function (p) {
      p.classList.remove('is-selected');
      p.setAttribute('aria-pressed', 'false');
    });
    pill.classList.add('is-selected');
    pill.setAttribute('aria-pressed', 'true');

    var unavailable = pill.getAttribute('data-unavailable') === 'true';
    var onSale = pill.getAttribute('data-on-sale') === 'true';
    var discountType = cfg(pill, 'discount-type', 'percent');
    var discountSuffix = cfg(pill, 'discount-suffix', 'Off');

    var priceMain = card.querySelector('[data-om-price-main]');
    var priceCompare = card.querySelector('[data-om-price-compare]');
    var priceSave = card.querySelector('[data-om-price-save]');

    if (priceMain) priceMain.textContent = pill.getAttribute('data-price') || '';

    if (priceCompare) {
      if (onSale) {
        priceCompare.textContent = pill.getAttribute('data-compare-price') || '';
        priceCompare.hidden = false;
      } else {
        priceCompare.hidden = true;
      }
    }

    if (priceSave) {
      if (onSale) {
        var amount = discountType === 'amount'
          ? pill.getAttribute('data-discount-amount')
          : pill.getAttribute('data-discount-percent') + '%';
        priceSave.textContent = amount + ' ' + discountSuffix;
        priceSave.hidden = false;
      } else {
        priceSave.hidden = true;
      }
    }

    var atc = card.querySelector('[data-om-atc]');
    if (atc) {
      atc.setAttribute('data-variant-id', pill.getAttribute('data-variant-id'));
      atc.disabled = unavailable;
      var label = atc.querySelector('[data-om-label]');
      if (label) {
        label.textContent = unavailable
          ? cfg(pill, 'sold-out-label', 'Sold out')
          : cfg(pill, 'label', 'Add to cart');
      }
    }
  });

  // ---------------------------------------------------------- wishlist ---

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-om-wishlist]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var id = btn.getAttribute('data-product-id');
    var active = btn.classList.contains('is-active');
    var list = getWishlist();

    list = active
      ? list.filter(function (x) { return x !== id; })
      : list.concat([id]);

    setWishlist(list);
    btn.classList.toggle('is-active', !active);
    btn.setAttribute('aria-pressed', String(!active));

    document.dispatchEvent(new CustomEvent('om:wishlist:toggled', {
      detail: {
        productId: id,
        productHandle: btn.getAttribute('data-product-handle'),
        active: !active
      }
    }));
  });

  // ------------------------------------------------------- add to cart ---

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-om-atc]');
    if (!btn) return;
    e.preventDefault();

    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId || btn.hasAttribute('disabled') || btn.classList.contains('is-loading')) return;

    var labelEl = btn.querySelector('[data-om-label]') || btn;
    var defaultLabel = cfg(btn, 'label', 'Add to cart');
    var addedLabel = cfg(btn, 'added-label', 'Added');
    var afterAdd = cfg(btn, 'after-add', 'drawer');
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    btn.classList.add('is-loading');

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        btn.classList.remove('is-loading');

        if (!res.ok) {
          labelEl.textContent = 'Unavailable';
          setTimeout(function () { labelEl.textContent = defaultLabel; }, 2200);
          return;
        }

        document.dispatchEvent(new CustomEvent('om:cart:added', { detail: res.data }));
        labelEl.textContent = addedLabel;
        setTimeout(function () { labelEl.textContent = defaultLabel; }, 2000);

        if (afterAdd === 'cart_page') {
          window.location = root + 'cart';
          return;
        }

        if (afterAdd === 'drawer') {
          // The custom header drawer owns the cart UI on this theme.
          if (window.OMCart && typeof window.OMCart.refreshAndOpen === 'function') {
            window.OMCart.refreshAndOpen();
            return;
          }
          var themeDrawer = document.querySelector('cart-drawer');
          if (themeDrawer && typeof themeDrawer.renderContents === 'function') {
            try {
              themeDrawer.renderContents(res.data);
              if (typeof themeDrawer.open === 'function') themeDrawer.open();
              return;
            } catch (err) { /* fall through */ }
          }
          window.location = root + 'cart';
          return;
        }

        refreshBubble();
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        labelEl.textContent = 'Error';
        setTimeout(function () { labelEl.textContent = defaultLabel; }, 2200);
      });
  });

  function refreshBubble() {
    fetch('/?sections=cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-icon-bubble'];
        var target = document.getElementById('cart-icon-bubble');
        if (!html || !target) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('#cart-icon-bubble') || doc.querySelector('.shopify-section');
        if (fresh) target.innerHTML = fresh.innerHTML;
      })
      .catch(function () {});
  }

  // --------------------------------------------------- slider arrows -----

  document.addEventListener('click', function (e) {
    var arrow = e.target.closest('[data-om-slide]');
    if (!arrow) return;

    var wrap = arrow.closest('.om-grid__wrap') || arrow.closest('[data-om-grid]');
    var grid = wrap && wrap.querySelector('.om-grid');
    if (!grid) return;

    var card = grid.querySelector('.om-card');
    var gap = parseFloat(window.getComputedStyle(grid).columnGap) || 0;
    var amount = card ? card.getBoundingClientRect().width + gap : grid.clientWidth * 0.8;
    var dir = arrow.getAttribute('data-om-slide') === 'next' ? 1 : -1;

    grid.scrollBy({ left: dir * amount, behavior: 'smooth' });
  });

  // ------------------------------------------------------------- boot ----

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paintWishlist);
  } else {
    paintWishlist();
  }

  // Facets re-render the grid; repaint hearts on the new markup.
  var t = null;
  new MutationObserver(function () {
    clearTimeout(t);
    t = setTimeout(paintWishlist, 200);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
